import { darken } from '@mui/material'
import Grid from '@mui/material/Grid'
import { StateButton } from 'components/buttons/styled'
import { useState } from 'react'
import { updateParticipantStatus } from 'services/api/event'
import LocalStorage from 'services/LocalStorage'
import { theme } from 'theme'
import { Attendee, EventDoc, EventParticipantStatus } from 'types/api/changes'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import { t } from 'i18next'

import {
  groupIdToProvider,
  isConvertedGroupId,
  ProviderType
} from 'utils/groups'
import { useEventPopover } from 'components/calendar/eventPopover/EventPopoverProvider'
import { isEventRecurrenceValid } from 'utils/event'
import { isEventRecurring } from 'utils/api/instance_state'
import useFitText from 'use-fit-text'

const darkenBgColor = darken(
  theme.palette.button.background.primary as string,
  0.1
)

interface GoingBarProps {
  setLoading: (b: boolean) => void
  event: Model<EventDoc> | EventDoc
  status: Record<string, EventParticipantStatus> | undefined
  setStatus: (s: Record<string, EventParticipantStatus>) => void
  attendees: Attendee[] | undefined
  setAttendees: (attendees: Attendee[]) => void
}

export function GoingBar(props: GoingBarProps) {
  const isThirdParty = isConvertedGroupId(props.event.GroupID)
  let userStatus =
    props.status?.[LocalStorage.get(LocalStorageKey.USER_ID) ?? '']
      ?.ParticipantConfirmationStatus ?? '1'

  if (props.attendees && isThirdParty) {
    userStatus = googleAttendeeStatusToGroupcal(
      props.attendees?.find((a) => a.self)?.responseStatus
    )
  }

  const [selectedState, setSelectedState] = useState<string>(userStatus)
  const { closePopover } = useEventPopover()
  const changeUserStatus = async (status: string) => {
    setSelectedState(status)
    if (isThirdParty) {
      props.setAttendees(
        props.attendees?.map((attendee) => {
          if (attendee.self)
            attendee.responseStatus =
              groupIdToProvider(props.event.GroupID ?? '') ===
              ProviderType.APPLE
                ? groupcalStatusToGoogle(status).toUpperCase()
                : groupcalStatusToGoogle(status)
          return attendee
        }) ?? []
      )
    } else {
      const updatedStatus =
        props.event.ParticipantsStatus?.[
          LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
        ]
      if (updatedStatus) {
        updatedStatus.ParticipantConfirmationStatus = status

        props.setLoading(true)
        if (isEventRecurring(props.event)) closePopover()
        await updateParticipantStatus([
          {
            _id: props.event._id,
            DeviceChangeID: LocalStorage.get(LocalStorageKey.DEVICE_ID) ?? '',
            ParticipantsStatus: { ...updatedStatus }
          }
        ])
        props.setLoading(false)
      }
    }
  }
  const regularColor = theme.palette.button.text.primary
  const yesTextColor =
    selectedState === '2' ? theme.palette.button.text.approve : regularColor
  const noTextColor =
    selectedState === '3' ? theme.palette.button.text.negative : regularColor
  const maybeTextColor =
    selectedState === '4' ? theme.palette.button.text.tenative : regularColor

  const yesBgColor =
    selectedState === '2'
      ? darkenBgColor
      : theme.palette.button.background.primary
  const noBgColor =
    selectedState === '3'
      ? darkenBgColor
      : theme.palette.button.background.primary
  const maybeBgColor =
    selectedState === '4'
      ? darkenBgColor
      : theme.palette.button.background.primary

  return (
    <Grid container direction={'row'} gap={1} id={'event-details-going-bar'}>
      <GoingStateButton
        text={t('accept')}
        bgColor={yesBgColor}
        statecolor={yesTextColor}
        onClick={changeUserStatus.bind(null, '2')}
      />

      <GoingStateButton
        text={t('decline')}
        bgColor={noBgColor}
        statecolor={noTextColor}
        onClick={changeUserStatus.bind(null, '3')}
      />
      <GoingStateButton
        text={t('maybe')}
        bgColor={maybeBgColor}
        statecolor={maybeTextColor}
        onClick={changeUserStatus.bind(null, '4')}
      />
    </Grid>
  )
}

function GoingStateButton({
  bgColor,
  statecolor,
  onClick,
  text
}: {
  bgColor: string | undefined
  statecolor: any
  onClick: () => void
  text: string
}) {
  const { fontSize, ref } = useFitText({
    minFontSize: 50,
    maxFontSize: 100
  })

  return (
    <StateButton bgcolor={bgColor} statecolor={statecolor} onClick={onClick}>
      <div ref={ref} style={{ fontSize: fontSize, width: '100%' }}>
        {text}
      </div>
    </StateButton>
  )
}

export function googleAttendeeStatusToGroupcal(
  responseStatus: string | undefined
): string {
  switch (responseStatus) {
    case 'declined':
    case 'DECLINED':
      return '3'
    case 'TENTATIVE':
    case 'tentative':
      return '4'
    case 'ACCEPTED':
    case 'accepted':
      return '2'
    default:
      return '1'
  }
}

export function groupcalStatusToGoogle(responseStatus: string): string {
  switch (responseStatus) {
    case '3':
      return 'declined'
    case '4':
      return 'tentative'
    case '2':
      return 'accepted'
    default:
      return 'needsAction'
  }
}
